import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import { css } from '@emotion/react';
import { useEuiMemoizedStyles } from '../../services';
import { getTokenName } from '@elastic/eui-theme-common';
export var BACKGROUND_COLORS = ['transparent', 'plain', 'subdued', 'highlighted', 'accent', 'accentSecondary', 'primary', 'success', 'warning', 'danger'];
/**
 * @deprecated - use background tokens directly
 * @returns A single background color with optional alpha transparency
 */
export var euiBackgroundColor = function euiBackgroundColor(_ref, color) {
  var euiTheme = _ref.euiTheme;
  var _ref2 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
    method = _ref2.method;
  if (color === 'transparent') return 'transparent';
  if (method === 'transparent') {
    var tokenName = getTokenName('backgroundTransparent', color);
    return euiTheme.colors[tokenName];
  } else {
    var _tokenName = getTokenName('backgroundBase', color);
    return euiTheme.colors[_tokenName];
  }
};

/**
 * @deprecated
 * @returns An object map of color keys to color values, categorized by
 * opaque (default) vs transparency (hover/focus states) methods.
 * e.g. {
 *  opaque: { danger: '#000', success: '#fff', ... },
 *  transparent: { danger: 'rgba(0,0,0,0.1)', success: 'rgba(255,255,255,0.1)', ... },
 * }
 */
var _euiBackgroundColorMap = function _euiBackgroundColorMap(euiThemeContext) {
  return {
    opaque: BACKGROUND_COLORS.reduce(function (acc, color) {
      return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, color, euiBackgroundColor(euiThemeContext, color)));
    }, {}),
    transparent: BACKGROUND_COLORS.reduce(function (acc, color) {
      return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, color, euiBackgroundColor(euiThemeContext, color, {
        method: 'transparent'
      })));
    }, {})
  };
};

/**
 * @deprecated
 */
export var useEuiBackgroundColor = function useEuiBackgroundColor(color) {
  var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    method = _ref3.method;
  var backgroundColorMap = useEuiMemoizedStyles(_euiBackgroundColorMap);
  return backgroundColorMap[method || 'opaque'][color];
};

/**
 * @returns An object map of color keys to CSS,
 * e.g. { danger: css``, success: css``, ... }
 */
var _euiBackgroundColors = function _euiBackgroundColors(euiThemeContext) {
  return BACKGROUND_COLORS.reduce(function (acc, color) {
    var tokenName = getTokenName('backgroundBase', color);
    var backgroundColor = color === 'transparent' ? 'transparent' : euiThemeContext.euiTheme.colors[tokenName];
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, color, /*#__PURE__*/css("background-color:", backgroundColor, ";label:", color, ";")));
  }, {});
};

/**
 * Hook to retrieve background style for a background color variant
 * @returns An object map of color keys to CSS,
 * e.g. { danger: css``, success: css``, ... }
 */
export var useEuiBackgroundColorCSS = function useEuiBackgroundColorCSS() {
  return useEuiMemoizedStyles(_euiBackgroundColors);
};

/**
 * Border colors
 * @deprecated - use border tokens directly or use
 * `useEuiBorderColorCSS()` for composed styles
 */

export var euiBorderColor = function euiBorderColor(_ref4, color) {
  var euiTheme = _ref4.euiTheme;
  switch (color) {
    case 'transparent':
      return euiTheme.border.color;
    default:
      {
        // border tokens are overridden in for high contrast mode
        // in high_contrast_overrides.ts
        var tokenName = getTokenName('borderBase', color);
        return euiTheme.colors[tokenName];
      }
  }
};

/**
 * @returns An object map of color keys to CSS,
 * e.g. { danger: css``, success: css``, ... }
 */
var _euiBorderColors = function _euiBorderColors(euiThemeContext) {
  return BACKGROUND_COLORS.reduce(function (acc, color) {
    // border tokens are overridden in for high contrast mode
    // in high_contrast_overrides.ts
    var borderToken = getTokenName('borderBase', color);
    var borderColor = color === 'transparent' ? euiThemeContext.euiTheme.border.color : euiThemeContext.euiTheme.colors[borderToken];
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, color, /*#__PURE__*/css("border-color:", borderColor, ";label:", color, ";")));
  }, {});
};

/**
 * Hook to retrieve border style for a border variant
 * @returns An object map of color keys to CSS,
 * e.g. { danger: css``, success: css``, ... }
 */
export var useEuiBorderColorCSS = function useEuiBorderColorCSS() {
  return useEuiMemoizedStyles(_euiBorderColors);
};